import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  FileText, CheckCircle, Clock, Package, ChevronRight,
  LogOut, ExternalLink, Briefcase, UserCheck, RefreshCw
} from "lucide-react";
import TicketChat from "../components/TicketChat";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
import Logo from "../components/Logo";

const STAGES = [
  { key: "inquiry_submitted", label: "Inquiry Submitted", icon: FileText },
  { key: "under_review", label: "Under Review", icon: Clock },
  { key: "plan_confirmed", label: "Plan Confirmed", icon: Package },
  { key: "fully_booked", label: "Fully Booked", icon: CheckCircle },
];

const STATUS_COLORS = {
  inquiry_submitted: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  under_review: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  plan_confirmed: { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  fully_booked: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
};

function StatusBadge({ status }) {
  const col = STATUS_COLORS[status] || { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" };
  const stage = STAGES.find(s => s.key === status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${col.bg} ${col.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${col.dot}`} /> {stage?.label || status}
    </span>
  );
}

function WorkerTicketCard({ ticket, onClaim, onStatusUpdate }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("details"); // "details" | "chat"
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [localTicket, setLocalTicket] = useState(ticket);
  const { user } = useAuth();

  const isAssignedToMe = localTicket.assigned_to === user?.user_id;

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const res = await axios.patch(`${API}/worker/tickets/${localTicket.ticket_id}/status`,
        { status: newStatus }, { withCredentials: true });
      setLocalTicket(res.data);
      onStatusUpdate(res.data);
    } catch {}
    setUpdatingStatus(false);
  };

  return (
    <div className="glass-card rounded-3xl overflow-hidden hover-lift" data-testid={`worker-ticket-${ticket.ticket_id}`}>
      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#0B3C5D] flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-black text-[#0B3C5D] font-outfit text-lg">{localTicket.ticket_id}</span>
                <StatusBadge status={localTicket.status} />
                {isAssignedToMe && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                    <UserCheck className="w-3 h-3" /> Assigned to me
                  </span>
                )}
              </div>
              <p className="text-sm text-[#6B7280] mt-0.5">
                {localTicket.treatment} · {localTicket.name} · {new Date(localTicket.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!localTicket.assigned_to && (
              <button onClick={() => onClaim(localTicket.ticket_id)}
                data-testid={`claim-btn-${localTicket.ticket_id}`}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#3FA7D6] text-white text-xs font-semibold rounded-xl hover:bg-[#0B3C5D] transition-colors">
                <UserCheck className="w-3.5 h-3.5" /> Claim Ticket
              </button>
            )}
            <button onClick={() => setOpen(!open)}
              className="flex items-center gap-1.5 text-sm font-semibold text-[#3FA7D6] hover:text-[#0B3C5D] transition-colors">
              {open ? "Close" : "Open"} <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            {["details", "chat"].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`flex-1 py-3 text-sm font-semibold capitalize transition-colors ${tab === t ? "text-[#0B3C5D] border-b-2 border-[#0B3C5D] bg-white" : "text-[#6B7280] hover:text-[#0B3C5D] bg-[#F8FAFC]"}`}>
                {t === "chat" ? "Messages" : "Details"}
              </button>
            ))}
          </div>

          {tab === "details" && (
            <div className="p-6 bg-[#F8FAFC]">
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                {[["Patient", localTicket.name], ["Email", localTicket.email],
                  ["Phone", localTicket.phone], ["Treatment", localTicket.treatment],
                  ["Language", localTicket.language?.toUpperCase()],
                  ["Submitted", new Date(localTicket.created_at).toLocaleDateString()]
                ].map(([l, v]) => (
                  <div key={l} className="glass-panel p-3.5 border-none">
                    <p className="text-xs text-[#6B7280] mb-0.5">{l}</p>
                    <p className="text-sm font-semibold text-[#0B3C5D]">{v}</p>
                  </div>
                ))}
                {localTicket.message && (
                  <div className="col-span-2 glass-panel p-3.5 border-none">
                    <p className="text-xs text-[#6B7280] mb-0.5">Patient Message</p>
                    <p className="text-sm text-gray-700">{localTicket.message}</p>
                  </div>
                )}
              </div>

              {isAssignedToMe && (
                <div>
                  <p className="text-xs font-semibold text-[#0B3C5D] uppercase tracking-wide mb-2">Update Status</p>
                  <div className="grid grid-cols-2 gap-2">
                    {STAGES.map(s => (
                      <button key={s.key} onClick={() => handleStatusChange(s.key)}
                        disabled={updatingStatus}
                        className={`py-2.5 px-3 rounded-xl text-xs font-semibold border-2 transition-all ${localTicket.status === s.key ? "border-[#0B3C5D] bg-[#0B3C5D] text-white" : "border-gray-200 text-gray-600 hover:border-[#3FA7D6] hover:text-[#3FA7D6]"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "chat" && (
            <div className="p-4">
              <TicketChat ticketId={localTicket.ticket_id} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WorkerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("assigned"); // "assigned" | "available"
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [availableTickets, setAvailableTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, vRes] = await Promise.all([
        axios.get(`${API}/worker/tickets`, { withCredentials: true }),
        axios.get(`${API}/worker/available`, { withCredentials: true }),
      ]);
      setAssignedTickets(aRes.data);
      setAvailableTickets(vRes.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleClaim = async (ticketId) => {
    try {
      await axios.post(`${API}/worker/tickets/${ticketId}/claim`, {}, { withCredentials: true });
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not claim ticket.");
    }
  };

  const handleStatusUpdate = (updated) => {
    setAssignedTickets(prev => prev.map(t => t.ticket_id === updated.ticket_id ? updated : t));
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const displayTickets = tab === "assigned" ? assignedTickets : availableTickets;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-30">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/"><Logo /></Link>
            <div className="hidden sm:block w-px h-5 bg-gray-200" />
            <div className="hidden sm:flex items-center gap-1.5">
              <Briefcase className="w-4 h-4 text-[#3FA7D6]" />
              <span className="text-sm font-semibold text-[#0B3C5D]">Care Coordinator Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border-2 border-[#3FA7D6]" />}
            <span className="text-sm font-medium text-[#0B3C5D] hidden sm:block">{user?.name}</span>
            <Link to="/" className="text-xs text-[#6B7280] hover:text-[#3FA7D6] transition-colors flex items-center gap-1">
              <ExternalLink className="w-3.5 h-3.5" /> Site
            </Link>
            <button onClick={handleLogout} data-testid="worker-logout-button"
              className="text-xs text-[#6B7280] hover:text-red-500 transition-colors flex items-center gap-1">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 py-10">
        {/* Welcome */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-black text-[#0B3C5D] font-outfit" data-testid="worker-dashboard-title">
              Welcome, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-[#6B7280] mt-1 text-sm">Manage your assigned tickets and support patients.</p>
          </div>
          <button onClick={fetchAll} className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#3FA7D6] transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Assigned to Me</p>
            <p className="text-4xl font-black text-[#0B3C5D] font-outfit">{assignedTickets.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Available</p>
            <p className="text-4xl font-black text-[#3FA7D6] font-outfit">{availableTickets.length}</p>
          </div>
          <div className="hidden sm:block bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">My Role</p>
            <p className="text-sm font-bold text-[#0B3C5D] mt-2 flex items-center gap-1.5">
              <Briefcase className="w-4 h-4 text-[#3FA7D6]" />
              {user?.role === "admin" ? "Admin / Worker" : "Care Coordinator"}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { key: "assigned", label: `My Tickets (${assignedTickets.length})` },
            { key: "available", label: `Available (${availableTickets.length})` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`worker-tab-${t.key}`}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === t.key ? "bg-[#0B3C5D] text-white shadow-sm" : "bg-white text-[#6B7280] border border-gray-200 hover:border-[#3FA7D6] hover:text-[#3FA7D6]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Ticket List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#3FA7D6] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : displayTickets.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100" data-testid="no-worker-tickets">
            <div className="w-16 h-16 rounded-full bg-[#3FA7D6]/10 flex items-center justify-center mx-auto mb-5">
              {tab === "assigned" ? <Briefcase className="w-8 h-8 text-[#3FA7D6]" /> : <UserCheck className="w-8 h-8 text-[#3FA7D6]" />}
            </div>
            <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit mb-2">
              {tab === "assigned" ? "No assigned tickets yet" : "No available tickets"}
            </h3>
            <p className="text-[#6B7280] text-sm">
              {tab === "assigned" ? "Switch to 'Available' to claim a ticket." : "All tickets are currently assigned."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {displayTickets.map(ticket => (
              <WorkerTicketCard
                key={ticket.ticket_id}
                ticket={ticket}
                onClaim={handleClaim}
                onStatusUpdate={handleStatusUpdate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
